/* eslint-disable no-underscore-dangle */
import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ReactTable from 'react-table';
import { get, isEmpty } from 'lodash';
import DailyTotalsModal from '../DailyTotals/DailyTotalsModal';

import { SubSummary } from './Sale';
import Date from '../../../common/date';
import DownloadButton from '../Download';
import RowExpanded from './SummaryRowExpanded';
import { MAX_SUMMARY_ENTRIES } from '../constants';
import DatePicker from '../../../common/DatePicker';
import RefundModal from '../../../common/RefundModal';
import * as commonShapes from '../../../common/shapes';
import DateSwitcher from '../../../common/DateSwitcher';
import ReassignButton from '../Reassign/ReassignButtonContainer';
import { canUserViewDailySummaryHistory } from '../../../common/helpers/users';
import {
  isReceipt,
  isRefundable,
  isOnlyFullRefund,
  getTableColumns,
  canGetReceipt,
  canReassign,
} from './utils';
import SelectReader from '../../webReader';
import Num from '../../../common/Num';

const afterReassign = (setSelected, setExpanded) => () => {
  setSelected({});
  setExpanded({});
};

const valueWithSummaryPermissions = (user, permissions, valueIfAllowed, valueIfForbidden, shop = null) => (
  canUserViewDailySummaryHistory(user, permissions, shop) ? valueIfAllowed : valueIfForbidden
);
const isSubItemSelected = (clickedItem, selected) => !!clickedItem.items.find(item => item.id === selected);

const Summary = ({
  items,
  numberOfTickets,
  refund,
  currentDate,
  setCurrentDate,
  endDate,
  expanded,
  setExpanded,
  isLoading,
  selected,
  setSelected,
  refundModalIsOn,
  refundModalOn,
  refundModalOff,
  dailyTotalsModalIsOn,
  dailyTotalsModalOn,
  dailyTotalsModalOff,
  bookingDepositsOn,
  bookingDepositsOff,
  bookingDepositsIsOn,
  paymentDepositsEnabled,
  permissions,
  getReceipt,
  user,
  shop,
  activeType,
  ownerId,
  ownerType,
  timezone,
}) => {
  const shopId = get(shop, ['id']);
  const numberOfDepositsTickets = items.filter(item => item.depositAmount).length;

  return (
    <div className="c-register-container">
      <div className="o-flexbox c-register-header">
        <div className="o-flexbox__item o-flexbox__item--flex-1 u-co-primary">
          <DatePicker
            component="button"
            onChange={setCurrentDate}
            startDate={valueWithSummaryPermissions(user, permissions, null, endDate, shop)}
            endDate={moment(endDate)
              .toDate()}
            day={currentDate}
            iconSize="medium"
            disabled={isLoading}
            display-if={canUserViewDailySummaryHistory(user, permissions, shop)}
          />
          <DownloadButton currentDate={currentDate} />
        </div>
        <div className="o-flexbox__item o-flexbox__item--flex-1">
          <DateSwitcher
            changeDate={setCurrentDate}
            currentDate={currentDate}
            startDate={valueWithSummaryPermissions(user, permissions, null, endDate, shop)}
            endDate={endDate}
            disabled={isLoading}
            className="o-flexbox--justify-center"
          />
        </div>
        <div className="o-flexbox__item o-flexbox__item--flex-2 u-text-right">
          {
            bookingDepositsIsOn
            && (
            <button
              type="button"
              className="c-btn c-btn--medium u-width-140 u-mr"
              onClick={() => bookingDepositsOff()}
            >
              Transactions
            </button>
            )
          }
          {
            !bookingDepositsIsOn && paymentDepositsEnabled
            && (
            <button
              type="button"
              className="c-btn c-btn--medium u-width-140 u-mr"
              onClick={() => bookingDepositsOn()}
            >
              Booking Deposits
            </button>
            )
          }
          <button
            type="button"
            className="c-btn c-btn--medium u-width-120 u-mr"
            onClick={() => dailyTotalsModalOn()}
          >
            Daily totals
          </button>
          <ReassignButton
            selected={selected}
            afterReassign={afterReassign(setSelected, setExpanded)}
            isLoading={isLoading}
            disabled={!canReassign(selected)}
          />
          <button
            type="button"
            className="c-btn c-btn--medium u-width-120"
            onClick={() => getReceipt(selected)}
            disabled={isEmpty(selected) || isLoading || !canGetReceipt(selected)}
            data-automation-id="button:receipt"
          >
            Receipt
          </button>
          <button
            type="button"
            className="c-btn c-btn--medium c-btn--red u-width-120 u-ml"
            onClick={refundModalOn}
            disabled={isEmpty(selected) || isLoading || !isRefundable(selected) || isReceipt(selected)}
            data-automation-id="button:refund"
          >
            Refund
          </button>
          <RefundModal
            title="Refund"
            close={refundModalOff}
            item={selected}
            refund={refund}
            canCustomRefund={!isOnlyFullRefund(selected)}
            display-if={refundModalIsOn}
          />

          {
            dailyTotalsModalIsOn
            && (
            <DailyTotalsModal
              title="Daily totals"
              shopId={shopId}
              isOpen={dailyTotalsModalIsOn}
              onClose={dailyTotalsModalOff}
              currentDate={currentDate}
              ownerType={ownerType}
              ownerId={ownerId}
              timezone={timezone}
            />
            )
          }
        </div>
      </div>
      <div className="o-flexbox u-mb">
        <div className="o-flexbox__item o-flexbox__item--flex-1">
          {!bookingDepositsIsOn && <span className="c-label">Transactions</span>}
          {bookingDepositsIsOn && <span className="c-label">Booking Deposits</span>}
        </div>
        <div className="o-flexbox__item o-flexbox__item--flex-1 u-text-center">
          {!bookingDepositsIsOn
            && <span className="c-label c-label--big">Number of Tickets: <Num>{numberOfTickets}</Num></span>}
          {bookingDepositsIsOn
            && <span className="c-label c-label--big">Number of Deposits: <Num>{numberOfDepositsTickets}</Num></span>}
        </div>
        <div className="o-flexbox__item o-flexbox__item--flex-2">&nbsp;</div>
      </div>
      {!bookingDepositsIsOn && (
      <ReactTable
        loading={isLoading}
        defaultPageSize={MAX_SUMMARY_ENTRIES}
        showPagination={false}
        defaultSorted={[{ id: 'time' }]}
        minRows={0}
        className="ReactTable--row-spacing-only ReactTable--sticky-header c-summary-table"
        data={items}
        expanded={expanded}
        getTrProps={(state, rowInfo) => ({
          onClick: () => {
            const expandedRows = {
              [rowInfo.viewIndex]: !expanded[rowInfo.viewIndex],
            };
            if (rowInfo.original.type === 'payout') {
              setExpanded(expandedRows);
              setSelected({});
              return;
            }
            if (rowInfo.original.type === 'recurring_membership') {
              setExpanded(expandedRows);
              setSelected(rowInfo.original);
              return;
            }

            const clickedItem = rowInfo.original;
            const isReceiptClicked = isReceipt(clickedItem);
            const isDeselecting = (isReceiptClicked && selected.id === clickedItem.id)
              || (!isReceiptClicked && selected.id === clickedItem.items[0].id);
            if (!isDeselecting && isReceipt(clickedItem) && isSubItemSelected(clickedItem, selected.id)) {
              setSelected(clickedItem);
              return;
            }

            setExpanded(expandedRows);
            let itemToSelect = {};
            if (!isDeselecting) {
              itemToSelect = isReceipt(rowInfo.original) ? rowInfo.original : rowInfo.original.items[0];
            }
            setSelected(itemToSelect);
          },
          className: classnames('is-clickable', {
            'is-active': (
              isReceipt(rowInfo.original) && expanded[rowInfo.viewIndex] && selected.id === rowInfo.original.id
            ),
          }),
          'data-automation-id': `summaryRow:${rowInfo.original.items?.[0]?.id ?? rowInfo.original.id}`,
        })}
        getTrGroupProps={(state, rowInfo) => ({
          className: classnames('is-clickable', {
            'is-active': !isReceipt(rowInfo.original) && expanded[rowInfo.viewIndex],
            'is-active-receipt': isReceipt(rowInfo.original) && expanded[rowInfo.viewIndex],
          }),
        })}
        SubComponent={(props) => {
          const original = get(props, 'original');
          if (isReceipt(original)) {
            return (
              <SubSummary
                items={original.items}
                collapseAll={(original.id === selected.id)}
                onItemClick={(row) => {
                  setSelected(selected.id !== row.original.id ? row.original : {});
                }}
              />
            );
          }

          if (original.type === 'payout') return null;

          if (original.type === 'recurring_membership') {
            return <RowExpanded item={original} />;
          }

          return <RowExpanded item={original.items[0]} />;
        }}
        columns={getTableColumns(activeType)}
        NoDataComponent={() => (isLoading
          ? null
          : (
            <p className="u-flex u-justify-content-center u-align-items-center u-mt+">
              There are no sales for the selected date
            </p>
          )
        )}
        TheadComponent={props => (
          (items && items.length)
            ? <ReactTable.defaultProps.TheadComponent {...props} />
            : null
        )}
      />
      )}
      {bookingDepositsIsOn && paymentDepositsEnabled && (
      <ReactTable
        loading={isLoading}
        defaultPageSize={MAX_SUMMARY_ENTRIES}
        showPagination={false}
        defaultSorted={[{ id: 'time' }]}
        minRows={0}
        className="ReactTable--row-spacing-only ReactTable--sticky-header c-summary-table"
        data={items.filter(item => item.depositAmount)}
        expanded={expanded}
        getTrProps={(state, rowInfo) => ({
          onClick: () => {
            const expandedRows = {
              [rowInfo.viewIndex]: !expanded[rowInfo.viewIndex],
            };
            if (rowInfo.original.type === 'payout') {
              setExpanded(expandedRows);
              setSelected({});
              return;
            }
            if (rowInfo.original.type === 'recurring_membership') {
              setExpanded(expandedRows);
              setSelected(rowInfo.original);
              return;
            }

            const clickedItem = rowInfo.original;
            const isReceiptClicked = isReceipt(clickedItem);
            const isDeselecting = (isReceiptClicked && selected.id === clickedItem.id)
              || (!isReceiptClicked && selected.id === clickedItem.items[0].id);
            if (!isDeselecting && isReceipt(clickedItem) && isSubItemSelected(clickedItem, selected.id)) {
              setSelected(clickedItem);
              return;
            }

            setExpanded(expandedRows);
            let itemToSelect = {};
            if (!isDeselecting) {
              itemToSelect = isReceipt(rowInfo.original) ? rowInfo.original : rowInfo.original.items[0];
            }
            setSelected(itemToSelect);
          },
          className: classnames('is-clickable', {
            'is-active': (
              isReceipt(rowInfo.original) && expanded[rowInfo.viewIndex] && selected.id === rowInfo.original.id
            ),
          }),
        })}
        getTrGroupProps={(state, rowInfo) => ({
          className: classnames('is-clickable', {
            'is-active': !isReceipt(rowInfo.original) && expanded[rowInfo.viewIndex],
            'is-active-receipt': isReceipt(rowInfo.original) && expanded[rowInfo.viewIndex],
          }),
        })}
        SubComponent={(props) => {
          const original = get(props, 'original');
          if (isReceipt(original)) {
            return (
              <SubSummary
                items={original.items}
                collapseAll={(original.id === selected.id)}
                onItemClick={(row) => {
                  setSelected(selected.id !== row.original.id ? row.original : {});
                }}
              />
            );
          }

          if (original.type === 'payout') return null;

          if (original.type === 'recurring_membership') {
            return <RowExpanded item={original} />;
          }

          return <RowExpanded item={original.items[0]} />;
        }}
        columns={getTableColumns(activeType, true)}
        NoDataComponent={() => (isLoading
          ? null
          : (
            <p className="u-flex u-justify-content-center u-align-items-center u-mt+">
              There are no sales for the selected date
            </p>
          )
        )}
        TheadComponent={props => (
          (items && items.length)
            ? <ReactTable.defaultProps.TheadComponent {...props} />
            : null
        )}
      />
      )}
      <SelectReader />
    </div>
  );
};

Summary.propTypes = {
  items: PropTypes.array.isRequired,
  numberOfTickets: PropTypes.number.isRequired,
  refund: PropTypes.func.isRequired,
  setCurrentDate: PropTypes.func.isRequired,
  currentDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
  expanded: PropTypes.object.isRequired,
  setExpanded: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  selected: PropTypes.object.isRequired,
  setSelected: PropTypes.func.isRequired,
  refundModalIsOn: PropTypes.bool.isRequired,
  refundModalOn: PropTypes.func.isRequired,
  refundModalOff: PropTypes.func.isRequired,
  dailyTotalsModalIsOn: PropTypes.bool.isRequired,
  dailyTotalsModalOn: PropTypes.func.isRequired,
  dailyTotalsModalOff: PropTypes.func.isRequired,
  bookingDepositsOn: PropTypes.func.isRequired,
  bookingDepositsOff: PropTypes.func.isRequired,
  bookingDepositsIsOn: PropTypes.bool.isRequired,
  paymentDepositsEnabled: PropTypes.bool.isRequired,
  permissions: commonShapes.permissions.isRequired,
  getReceipt: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  shop: PropTypes.object,
  activeType: PropTypes.string.isRequired,
  ownerId: PropTypes.string.isRequired,
  ownerType: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
};

Summary.defaultProps = {
  shop: null,
};

export default Summary;
